import { TextCodes } from '@api/types/translation/TextCodes';
import { Box, IconButton, styled, TextField, useMediaQuery, useTheme } from '@mui/material';
import { t } from '@translations';

import { useGetDealerListQuery } from '@api/customer.api';
import {
  Button,
  Image,
  LocationButton,
  PlacesAutocomplete,
  ProductDetailsContext,
  SearchDealerAutocomplete,
  SearchDealerValue,
  Text,
} from '@components';
import { useAppSelector, useToast } from '@hooks';
import { Close } from '@mui/icons-material';
import { getModalState, setModalState } from '@store/modal/modal.slice';
import { formatPrice } from '@utils/price.utils';
import { useCallback, useContext, useState } from 'react';

import { useAddProductOrderRequestMutation } from '@api/productRequest.api';
import { Dealer } from '@api/types';
import { LoadScriptProps, useLoadScript } from '@react-google-maps/api';
import { AppDispatch } from '@store';
import { getSpecialPage, getSystemPages } from '@store/app/app.slice';
import { Coordinates, getUserLocation, LocationState } from '@store/location/location.slice';
import { sortByProximity } from '@utils/location.utils';
import { useEffect } from 'react';
import { useForm, ValidationRule } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { GlobalStyles } from '@themes/global/GlobalStyles';

const libraries: LoadScriptProps['libraries'] = ['places'];

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(4),
  },
}));

const StyledTextField = styled(TextField)({
  '& legend span': {
    display: 'none',
  },
});

const StyledTextarea = styled(TextField)({
  '& legend span': {
    display: 'none',
  },
  '& .MuiOutlinedInput-root': {
    padding: '0 !important',
    height: 'auto',
  },
  '& textarea': {
    height: '91px !important',
    padding: '10.5px 12px',
  },
});

const modalFields = (): {
  name: keyof RequestOrderFormFields;
  label: string;
  required: boolean;
  type?: string;
  pattern?: ValidationRule<RegExp>;
}[] => [
  {
    name: 'selectDealer',
    label: t(TextCodes.ProductRequest.Order.SelectDealer),
    required: true,
  },
  {
    name: 'customerName',
    label: t(TextCodes.ProductRequest.Order.CustomerName),
    required: false,
    type: 'text',
  },
  {
    name: 'phoneNumber',
    label: t(TextCodes.ProductRequest.Order.PhoneNumber),
    required: true,
    type: 'tel',
    pattern: /^\+?[0-9-\s]+$/,
  },
  {
    name: 'email',
    label: t(TextCodes.ProductRequest.Order.Email),
    required: true,
    type: 'email',
    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  },
];

type RequestOrderModalProps = {
  modalId: string;
};

type RequestOrderFormFields = {
  selectDealer: Dealer;
  customerName: string;
  phoneNumber: string;
  email: string;
  deliveryNote: string;
};

export const AddRequestOrderModal = ({ modalId }: RequestOrderModalProps) => {
  const modalState = useAppSelector(getModalState(modalId));
  const dispatch = useDispatch<AppDispatch>();
  const locationState: LocationState = useSelector(getUserLocation);
  const { product = {} } = useContext(ProductDetailsContext);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitted },
  } = useForm<RequestOrderFormFields>();
  const { toast } = useToast();
  const [selectedDealer, setSelectedDealer] = useState<SearchDealerValue | null>(null);
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const systemPages = useSelector(getSystemPages);
  const theme = useTheme();
  const googleMapApiKey = systemPages.googleMapApiKey as string;
  const { isLoaded } = useLoadScript({
    region: 'no',
    language: 'no',
    googleMapsApiKey: googleMapApiKey,
    libraries,
  });
  const privacyPolicyPage = useAppSelector(getSpecialPage('privacyPolicyPage')) as string;
  const termAndConditionsPage = useAppSelector(getSpecialPage('termsAndConditionsPage')) as string;

  const closeModalHandler = useCallback(() => {
    dispatch(setModalState({ id: modalId, isVisible: false }));
    reset();
    setSelectedDealer(null);
  }, [dispatch, modalId, reset]);

  const [addProductOrderRequest, { isLoading }] = useAddProductOrderRequestMutation();
  const { currentData, isLoading: isDealersLoading } = useGetDealerListQuery({
    offSet: 0,
    limit: 1000,
    filters: [
      {
        filterName: 'SearchByName',        
      },
      {
        filterName: 'SearchByCustomerPriceGroup',
        value: product?.code
      }
    ],
  });

  const fetchedDealers = currentData?.data?.customersListFound;

  const handleSearchDealerChange = (_: any, newValue: SearchDealerValue) => {
    if (!Array.isArray(newValue)) {
      setSelectedDealer(newValue);
    }
  };

  const coordinatesHandler = (coordinates: Coordinates | null) => {
    setCoordinates(coordinates);
  };

  const onSubmit = async (data: RequestOrderFormFields) => {
    const formData = new FormData();
    formData.append('productCode', product?.code || '');
    formData.append('dealerId', (selectedDealer as Dealer)?.id || '');
    formData.append('dealerName', (selectedDealer as Dealer)?.name || '');
    formData.append('phoneNumber', data.phoneNumber || '');
    formData.append('email', data.email || '');
    formData.append('deliveryNote', data.deliveryNote || '');
    formData.append('customerName', data.customerName || '');
    try {
      await addProductOrderRequest(formData).unwrap();
      reset();
      closeModalHandler();
      toast({
        severity: 'success',
        message: t(TextCodes.ProductRequest.Base.Success),
      });
    } catch (error) {
      toast({
        severity: 'error',
        message: t(TextCodes.ProductRequest.Base.Error),
      });
    }
  };
  const isBreakepointMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (
      !coordinates &&
      locationState.permission === 'granted' &&
      locationState.location &&
      fetchedDealers
    ) {
      const { latitude, longitude } = locationState.location;
      const sortedDealers = sortByProximity({ latitude, longitude }, fetchedDealers);
      setDealers(sortedDealers);
      return;
    }
    if (coordinates && fetchedDealers) {
      const sortedDealers = sortByProximity(coordinates, fetchedDealers);
      setDealers(sortedDealers);
      return;
    }

    setDealers(fetchedDealers || []);
  }, [fetchedDealers, locationState, coordinates]);

  return (
    <BootstrapDialog
      id={modalId}
      open={modalState.isVisible}
      onClose={closeModalHandler}
      aria-labelledby='create-order-request'
      fullScreen={isBreakepointMobile}
      maxWidth='sm'
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          border: `1px solid ${theme.palette.primary.main}`,
        },
      }}
    >
      <GlobalStyles>
        <DialogTitle
          sx={{
            m: 0,
            p: {
              xs: 2,
              sm: 4,
            },
          }}
        >
          {t(TextCodes.ProductRequest.Order.ModalTitle)}
        </DialogTitle>
        <IconButton
          aria-label='close'
          onClick={closeModalHandler}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[900],
          })}
        >
          <Close />
        </IconButton>
        <DialogContent
          sx={{
            pt: '0 !important',
            px: {
              xs: '16px !important',
              sm: '32px !important',
            },
          }}
        >
          <Text sx={{ mb: 4 }}>{t(TextCodes.ProductRequest.Order.Preamble)}</Text>
          <Box
            className='product-order-element'
            display='flex'
            gap={2}
            p={4}
            bgcolor='white'
            border='1px solid black'
            width='100%'
          >
            <Box
              className='product-image-cell'
              sx={{
                '& img': {
                  height: '50px',
                  width: '50px',
                },
              }}
            >
              <Image
                image={product?.mainImage}
                className='product-image'
                resizerParams={{ width: 200, format: 'webp' }}
              />
            </Box>
            <div className='product-order-cell'>
              <div className='product-name'>{product?.name}</div>
              <div className='product-id'>{product?.productNo}</div>
            </div>
            <Box display='flex' alignItems='center' className='product-price-cell'>
              <div className='product-price'>{formatPrice(product?.discountPrice)}</div>
            </Box>
          </Box>
          <Box
            component='form'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '100%',
              mt: 6,
            }}
            noValidate
            autoComplete='off'
            onSubmit={handleSubmit(onSubmit)}
          >
            <Box sx={{ display: 'flex', mt: 2 }}>
              {isLoaded && <PlacesAutocomplete setCoordinates={coordinatesHandler} />}
              <LocationButton variant='text' />
            </Box>
            <SearchDealerAutocomplete
              sx={{ width: '100%', mt: 3 }}
              dealers={dealers}
              handleAutocompleteChange={handleSearchDealerChange}
              value={selectedDealer}
              currentLocation={coordinates || locationState.location}
              required
              error={isSubmitted && !selectedDealer}
              busy={isDealersLoading}
              disabled={isDealersLoading}
            />
            {modalFields()
              .filter((field) => field.name !== 'selectDealer')
              .map((field) => (
                <StyledTextField
                  sx={{ mt: 3 }}
                  key={field.name}
                  label={field.label}
                  variant='outlined'
                  required={field.required}
                  type={field.type}
                  {...register(field.name, { required: field.required, pattern: field.pattern })}
                  error={!!errors[field.name]}
                />
              ))}
            <StyledTextarea
              required
              label={t(TextCodes.ProductRequest.Order.DeliveryNote)}
              multiline
              maxRows={4}
              sx={{
                mt: 3,
              }}
              {...register('deliveryNote', { required: true })}
              error={!!errors.deliveryNote}
            />
            <div>
              <Text
                dangerouslySetInnerHTML={{
                  __html: t(TextCodes.ProductRequest.Order.Confirmation, {
                    privacyPolicy: `<a href="${privacyPolicyPage || ''}" target="_blank">${t(
                      TextCodes.ProductRequest.Order.PrivacyPolicy
                    )}</a>`,
                    termsAndConditions: `<a href="${
                      termAndConditionsPage || ''
                    }" target="_blank">${t(TextCodes.ProductRequest.Order.TermsAndConditions)}</a>`,
                  }),
                }}
              />
            </div>
            <Button
              variant='primary'
              type='submit'
              busy={isLoading}
              sx={{
                mt: 2,
                backgroundColor: theme.palette.primary.main,
                ':hover': {
                  backgroundColor: theme.palette.primary.light,
                },
              }}
            >
              {t(TextCodes.ProductRequest.Base.Submit)}
            </Button>
          </Box>
        </DialogContent>
      </GlobalStyles>
    </BootstrapDialog>
  );
};
